import assertNever from '@watershed/shared-util/src/assertNever';

import { FINANCE_ROUTE_PREFIX } from './finance/constants';

// Articles for each dataset should be added here
export const LearningHubArticlesForDatasetsMap = {
  OperatingExpenses: 'operating-expenses',
  Waste: 'waste',
  Vehicles: 'vehicles',
  EmployeeTravelExpenses: 'travel-expenses',
  Revenue: 'revenue',
  CloudCosts: 'cloud',
  CloudUsage: 'cloud',
  CloudUsageAndCost: 'cloud',
  CapitalExpenses: 'capital-expenses',
  Employees: 'employees',
  UpstreamLogistics: 'logistics',
  DownstreamLogistics: 'logistics',
  Flights: 'flights',
  Utilities: 'facilities-utilities-datasets',
  Buildings: 'facilities-utilities-datasets',
};
// All articles that we want to link to in the learning hub should be in this map
const LearningHubArticleSlugMap = {
  ...LearningHubArticlesForDatasetsMap,
  CarbonMethodologyUpdates: 'carbon-methodology-updates',
  UnderstandingEEIOModels: 'eeios-ceda-useeio',
  CustomizeEmissionsFactors: 'customize-emissions-factors',
  DataCollection: 'data-collection',
  DataCollectionFaqs: 'data-collection-faqs',
  CreateFootprint: 'how-to-create-a-footprint',
  NoDataForDataset:
    'data-collection-faqs#what-if-i-dont-have-any-data-for-this-dataset-4',
  AddingAdditionalDataWithCompanyTags: 'using-custom-columns',
  ApprovingDataForFootprintCalculation:
    'how-to-approve-data-for-use-in-footprint-calculations',
  HowToUseApprovalWorkflows: 'how-to-use-approval-workflows',
  Facilities: 'how-to-upload-your-facilities-buildings-and-utilities-data',
  FacilitiesUtilitiesDatasets: 'facilities-utilities-datasets', // New as part of FY25Q3 facilities approvals
  FacilitiesGapFilling:
    'how-to-upload-your-facilities-buildings-and-utilities-data#how-we-gap-fill-your-facilities-data-3',
  FinanceMethodologyUpdates:
    'introduction-to-watershed-finances-methodology-updates',
  FinanceMetricCalculations: 'finance-metric-calculations',
  FinanceDataExplorer: 'using-data-explorer',
  FinanceCustomReports: 'using-finance-custom-reports',
  FinanceConvergenceUpdate: 'finance-monthly-product-updates',
  FootprintReview: 'reviewing-a-footprint',
  Deduplication: 'how-to-deduplicate-your-footprint',
  Recategorization: 'how-to-recategorize-your-footprint',
  WatershedFootprintReview:
    'reviewing-a-footprint%23requesting-a-watershed-review-1',
  ApproveFootprint: 'completing-your-watershed-footprint',
  HowToUploadData: 'how-to-upload-data',
  ValidatingAndCorrectingDataErrors: 'validating-and-correcting-data-errors',
  ScienceBasedTargets: 'science-based-targets',
  NewDataLineage: 'footprint/[beta]-new-data-lineage',
  Scope1Methodology: 'how-we-measure-direct-emissions-scope-1',
  Scope2Methodology: 'how-we-measure-purchased-energy-emissions-scope-2',
  Scope31Methodology:
    'how-we-measure-emissions-from-purchased-goods-and-services-scope-31',
  Scope32Methodology: 'how-we-measure-emissions-from-capital-goods-scope-32',
  Scope33Methodology:
    'how-we-measure-emissions-from-fuel-and-energy-related-activities-scope-33',
  Scope34Methodology:
    'how-we-measure-emissions-from-upstream-transportation-and-distribution-scope-34',
  Scope35Methodology: 'how-we-measure-emissions-from-waste-scope-35',
  Scope36Methodology: 'how-we-measure-emissions-from-business-travel-scope-36',
  Scope37Methodology:
    'how-we-measure-emissions-from-employee-commuting-scope-37',
  Scope38Methodology:
    'how-we-measure-emissions-from-upstream-leased-assets-scope-38',
  Scope39Methodology:
    'how-we-measure-emissions-from-downstream-transportation-and-distribution-scope-39',
  Scope310Methodology: 'how-we-measure-emissions-from-investments-scope-310',
  Scope311Methodology:
    'how-we-measure-emissions-from-use-of-sold-products-scope-311',
  Scope312Methodology:
    'how-we-measure-emissions-for-end-of-life-treatment-of-sold-products-scope-312',
  Scope313Methodology:
    'how-we-measure-emissions-from-downstream-leased-assets-scope-313',
  Scope314Methodology: 'how-we-measure-emissions-from-franchises-scope-314',
  ScienceOfTheClimateCrisis: 'science-of-the-climate-crisis',
  SettingCompanyAmbition: 'setting-company-ambition',
  Measurements: 'carbon-accounting-and-measurement',
  SelectingFuelCombustionPurpose:
    'selecting-a-purpose-for-your-buildings-fuel-combustion',
  CleanPowerPercentGuide: 'measuring-clean-power-usage-on-building-utilities',
  GettingStartedWithMeasurement:
    'getting-started-with-measurement#setting-up-your-measurement-2',
  WaterMetricKinds: 'water-metric-kinds',
  AnomalyDetection: 'anomaly-detection',
  PreparingForSecr: 'preparing-for-secr',
  CdpAutoGeneratedMetrics:
    'using-the-watershed-cdp-report-builder-for-auto-generated-metrics',
  CreatingAndConfiguringYourCSRDReport:
    'creating-and-configuring-your-csrd-report',
  DoubleMaterialityAssessmentsUnderCSRD:
    'double-materiality-assessments-under-csrd',
  FinancialMapping: 'financial-mapping',
  FullListBeaCodes: 'full-list-of-bea-codes',
  NonEmissiveSpend: 'what-is-non-emissive-spend-',
  PCAFScoring: 'how-does-pcaf-scoring-work',
  PCAFAssetClasses: 'choosing-pcaf-asset-classes',
  FinancedEmissionsRoot: 'financed-emissions',
  FinancedEmissions: 'what-are-financed-emissions',
  ScopesOfEmissions: 'scopes-of-emissions',
  FinancedEmissionsFromEquityBondsLoans:
    'measuring-financed-emissions-from-equity-corporate-bonds-and-business-loans',
  ClimateTargetSetting: 'climate-target-setting',
  CompanyEstimationEngine: 'cee',
  CompanyEstimationEngineIndustryCodes: 'cee#required-inputs-3',
  FinancedEmissionsFromRealEstate:
    'measuring-financed-emissions-from-commercial-real-estate',
  ReportingAndRegulations: 'reporting-and-regulations',
  UploadingAnExistingFootprint: 'uploading-an-existing-footprint',
  CustomReports: 'build-a-report#create-custom-reports-2',
  ReducingSupplyChainEmissions:
    'reducing-supply-chain-emissions-and-how-to-engage-your-suppliers-',
  CreateCustomMaturityScoring: 'how-to-create-custom-maturity-scoring',
  CreateCustomMetrics: 'how-to-create-custom-metrics',
  CedaWatershedFinance: 'using-ceda-in-watershed-finance',
  LegacyData: 'legacy-data',
  SupplierMapping: 'supplier-mapping',
  HowToUploadADoubleMaterialityAssessment:
    'how-to-upload-a-double-materiality-assessment',
  Benchmarks: 'using-benchmarks',
  UnderstandingSustainabilityDatabase:
    'understanding-watersheds-sustainability-database',
  FootprintPermissions: 'understanding-watershed-permissions%23footprints-7',
  MultipleFootprintsBeta:
    '%5Bbeta%5D-how-to-use-multiple-footprints-november-2024',
  UnderstandingVerifications: 'understanding-verifications',
  OrgStructure: 'organizational-structure',
  ActivityData: 'what-is-activity-data',
  WatershedFinanceCompanyMatching: 'watershed-finance-company-matching',
  ProductFootprints: 'watershed-product-footprints',
  Api: 'api',
  ChangingLanguageLocale: 'changing-language-locale-on-watershed',
  aiDraftsInCaliforniaSb261: 'ai-drafts-in-california-sb-261',
  PolicyUpdates: 'policy-updates',
};

export type LearningHubArticle = keyof typeof LearningHubArticleSlugMap;

export const learningHubHashForArticle = (
  article: LearningHubArticle
): string => {
  return LearningHubArticleSlugMap[article];
};

// If an article changes links, add it to the redirect map here so any old links still work
const LEARNING_HUB_REDIRECTS: Record<string, string> = {
  // Consolidating articles into one central data-collections-faq page
  'what-if-i-dont-have-any-data-for-this-dataset':
    'data-collection-faqs#what-if-i-dont-have-any-data-for-this-dataset-4',
  'can-i-include-multiple-data-sources-under-each-dataset-':
    'data-collection-faqs#how-do-i-add-multiple-tasks-under-each-dataset-2',
  'how-to-add-or-remove-datasets-after-setting-up-a-measurement':
    'data-collection-faqs#how-do-i-add-or-remove-datasets-5',
  'reviewing-your-footprint': 'reviewing-a-footprint',
};

export const getLearningHubSlugWithRedirects = (slug: string): string =>
  LEARNING_HUB_REDIRECTS[slug] || slug;

// If you add a new place in the product that links to the learning hub, add it here for analytics
const _LEARNING_HUB_ENTRY_POINTS = [
  'LegacyActivityData',
  'Sidebar',
  'MethodologyExplainer',
  'SupplyChainCharts',
  'DatasourceInstructionsSidebar',
  'DatasourceSummaryHelpDialog',
  'DatasourceApprovalStatusDialog',
  'DataImporterHelpButton',
  'DataImporterLeaveDialog',
  'EmissionsFactorsPage',
  'EmissionsFactorsImporter',
  'MethodologyConfig',
  'SupplierPortalPage',
  'HomePage',
  'FootprintQuestionMenu',
  'RequestWatershedFootprintReview',
  'DeduplicationPage',
  'Facilities',
  'FootprintOverview',
  'UploadCompleteEmail',
  'FinanceHomepage',
  'FinanceSettings',
  'FinanceAssetSummary',
  'FinanceIndustryAutocomplete',
  'FinanceSandbox',
  'FinanceFootprintAssembly',
  'FinanceDataExplorer',
  'FinanceReporting',
  'MeasurementHomePage',
  'BuildingUtilitiesDialog',
  'CategoryRulesInfoTooltip',
  'RecategorizationIndexView',
  'CategoryRulesDetail',
  'SecrReportingComponent',
  'CdpTableView',
  'FinancialValueMappingPage',
  'FinanceProduct',
  'AnomalyDetectionCallout',
  'NewMeasurementWizard',
  'MeasurementConfiguration',
  'FootprintConfig',
  'DrilldownConfiguration',
  'FootprintCreation',
  'FootprintExports',
  'FootprintsList',
  'ImportExistingFootprint',
  'CalculationCustomization',
  'CustomTagsSetup',
  'DatasetsPage',
  'CustomReportWelcomeMessage',
  'ReportConfiguration',
  'ReportMaterialityPage',
  'SupplyChainHomePage',
  'SupplyChainMaturityScoringPage',
  'SupplierMapping',
  'MaterialityPage',
  'Benchmarks',
  'OrgStructure',
  'ActivityData',
  'SupplierResponses',
  'DataLineage',
  'MaterialUpload',
  'MaterialLibrary',
  'OrganizationSettings',
  'LocaleDialog',
  'AISandbox',
  'SupportCaseFAQ',
  'ReportPage',
  'CreateReportDialog',
  'AiReportDraftDialog',
  'PurchaseOrderMapping',
] as const;

export type LearningHubEntryPoint = (typeof _LEARNING_HUB_ENTRY_POINTS)[number];

export type LearningHubViewMode = 'regular' | 'finance' | 'fullPage';

export function learningHubBaseUrlForViewMode(
  viewMode: LearningHubViewMode
): string {
  switch (viewMode) {
    case 'regular':
      return '/learn';
    case 'finance':
      return FINANCE_ROUTE_PREFIX + '/learn';
    case 'fullPage':
      return '/learn/full';
    default:
      assertNever(viewMode);
  }
}
